import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Stepper, Step, StepLabel
} from '@mui/material';
import { Upload, Preview, CheckCircle } from '@mui/icons-material';

const steps = ['Select File', 'Preview Schema', 'Confirm Upload'];

/**
 * FeedUploadDialog — Two-step dialog: preview file schema, then confirm upload.
 */
const FeedUploadDialog = ({ open, feed, onClose, onConfirm, onPreview }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState(null);

  const isExcelFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext === 'xlsx' || ext === 'xls';
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) return;

    try {
      setPreviewLoading(true);
      setError(null);
      const result = await onPreview(selectedFile);
      setPreviewData(result);
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data || 'Failed to preview file. Please check the file format.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploadLoading(true);
      setError(null);
      await onConfirm(selectedFile);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data || 'Failed to upload file.');
    } finally {
      setUploadLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Data — {feed.displayName}
        <Typography variant="body2" color="text.secondary">
          Feed type: <code>{feed.feedType}</code>
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}

        {/* Step 0: File Selection */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select a CSV, TSV, TXT, or Excel file to upload.
              {' '}CSV/TSV/TXT files will be previewed before uploading. Excel files upload directly.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<Upload />}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".csv,.tsv,.txt,.xlsx,.xls"
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`${selectedFile.name} (${formatFileSize(selectedFile.size)})`}
                  color="primary"
                  variant="outlined"
                />
                {isExcelFile(selectedFile) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Excel files do not support schema preview. The file will be uploaded directly.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Step 1: Preview */}
        {activeStep === 1 && previewData && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`Delimiter: "${previewData.delimiter === '\t' ? 'TAB' : previewData.delimiter}"`} variant="outlined" />
              <Chip label={`${previewData.columns.length} columns`} variant="outlined" />
              <Chip label={`${previewData.totalRowCount.toLocaleString()} rows`} variant="outlined" color="primary" />
              <Chip label={formatFileSize(previewData.fileSizeBytes)} variant="outlined" />
            </Box>

            {previewData.quotingDetected && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Quoted fields detected. Column count may be inaccurate if fields contain the delimiter inside quotes.
                The ingestion pipeline handles quoting correctly — this preview is an approximation.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Detected Columns:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {previewData.columns.map((col) => (
                <Chip key={col} label={col} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Sample Rows (first {previewData.sampleRows.length}):
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {previewData.columns.map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.sampleRows.map((row, idx) => (
                    <TableRow key={idx}>
                      {previewData.columns.map((col) => (
                        <TableCell key={col} sx={{ fontSize: '0.75rem' }}>
                          {row[col] || ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 2: Success */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6">Upload Complete</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The ingestion pipeline will process the file automatically.
              Feed status will update once processing completes (typically 1–5 minutes).
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {selectedFile && isExcelFile(selectedFile) ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmUpload}
                disabled={uploadLoading}
                startIcon={uploadLoading ? <CircularProgress size={16} /> : <Upload />}
              >
                {uploadLoading ? 'Uploading...' : 'Upload'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handlePreview}
                disabled={!selectedFile || previewLoading}
                startIcon={previewLoading ? <CircularProgress size={16} /> : <Preview />}
              >
                {previewLoading ? 'Analyzing...' : 'Preview'}
              </Button>
            )}
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmUpload}
              disabled={uploadLoading}
              startIcon={uploadLoading ? <CircularProgress size={16} /> : <Upload />}
            >
              {uploadLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

FeedUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  feed: PropTypes.shape({
    feedId: PropTypes.string.isRequired,
    feedType: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
};

export default FeedUploadDialog;
